import { meetingBodyJoinDimensions, seatJoinDimensions } from './shared-dimensions.js';
import { seatTermStatusValueLabels } from './value-labels.js';
/**
 * Combination data source: Seats x Seat Terms x Members x Positions.
 * Complete roster of seat assignments showing who is serving (or has served) on each seat,
 * their position, term details, and contact information. Also serves as the historical
 * term view when filtered by date range.
 */
export const memberRosterCube = {
    category: 'Members',
    description: 'Complete roster of seat assignments showing who is serving, their position, and term details.',
    label: 'Member Roster',
    question: 'Who serves on each body, in what role, and when does their term end?',
    bestFor: 'Pairs each member with their seat, position title, term status, and days remaining — fields you cannot get from any other source.',
    tags: ['Members', 'Terms', 'Positions'],
    measures: {
        'member_roster.count': { label: 'Count', type: 'number' },
        'member_roster.distinct_members': { label: 'Distinct Members', type: 'number' },
        'member_roster.distinct_positions': { label: 'Distinct Positions', type: 'number' },
    },
    dimensions: {
        // Assignment
        'member_roster.end_reason': { label: 'End Reason', type: 'string' },
        'member_roster.notes': { label: 'Notes', narrative: true, type: 'string' },
        'member_roster.status': {
            label: 'Assignment Status',
            type: 'string',
            valueLabels: seatTermStatusValueLabels,
        },
        'member_roster.term_number': { label: 'Member Term Number', type: 'number' },
        ...meetingBodyJoinDimensions,
        // Member
        'meeting_members.avatar_url': {
            label: "Member's Avatar",
            renderAs: 'image',
            type: 'string',
        },
        'meeting_members.email': { label: 'Member Email', type: 'string' },
        'meeting_members.employer': { label: 'Member Employer', type: 'string' },
        'meeting_members.ethnicity': { label: 'Member Ethnicity', type: 'string' },
        'meeting_members.first_name': { label: 'Member First Name', type: 'string' },
        'meeting_members.has_user_account': {
            label: 'Member Has Linked User Account',
            type: 'boolean',
        },
        'meeting_members.last_name': { label: 'Member Last Name', type: 'string' },
        'meeting_members.notes': { label: 'Member Notes', narrative: true, type: 'string' },
        'meeting_members.occupation': { label: 'Member Occupation', type: 'string' },
        'meeting_members.website': { label: 'Member Website', type: 'string' },
        // Position
        'meeting_body_positions.title': { label: 'Position', type: 'string' },
        // Seat
        ...seatJoinDimensions,
        'meeting_body_seats.term_length': { label: 'Seat Term Length', type: 'number' },
        'meeting_body_seats.term_limit': { label: 'Seat Term Limit', type: 'number' },
        // Term
        'meeting_body_seat_terms.days_remaining': { label: 'Term Days Remaining', type: 'number' },
        'meeting_body_seat_terms.is_current': { label: 'Term Is Current', type: 'boolean' },
        'meeting_body_seat_terms.number': { label: 'Term Number', type: 'number' },
        'meeting_body_seat_terms.status': {
            label: 'Term Status',
            type: 'string',
            valueLabels: seatTermStatusValueLabels,
        },
        // Member contact (via phone join)
        'phones.number': { label: 'Member Phone Number', type: 'string' },
        // Member address (via addresses join)
        'addresses.address_1': { label: 'Member Address 1', type: 'string' },
        'addresses.address_2': { label: 'Member Address 2', type: 'string' },
        'addresses.city': { label: 'Member City', type: 'string' },
        'addresses.country_code': { label: 'Member Country Code', type: 'string' },
        'addresses.postal_code': { label: 'Member Postal Code', type: 'string' },
        'addresses.state': { label: 'Member State', type: 'string' },
    },
    timeDimensions: {
        'meeting_body_seat_terms.end_date': { label: 'Term End Date', type: 'date' },
        'meeting_body_seat_terms.start_date': { label: 'Term Start Date', type: 'date' },
        'meeting_body_seat_terms.vacancy_date': { label: 'Term Vacancy Date', type: 'date' },
        'meeting_members.created_at': { label: 'Member Created At', type: 'date' },
        'meeting_members.updated_at': { label: 'Member Updated At', type: 'date' },
        'member_roster.end_date': { label: 'Assignment End Date', type: 'date' },
        'member_roster.member_since': { label: 'Member Since', type: 'date' },
        'member_roster.start_date': { label: 'Assignment Start Date', type: 'date' },
    },
};
